import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output, ChangeDetectionStrategy
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {Router} from '@angular/router';
import {firstValueFrom, Observable, of} from 'rxjs';
import {map, switchMap, take, takeWhile} from 'rxjs/operators';
import { isEqual } from 'lodash-es';

import { Product } from '../../../models/product.model';
import { Order } from '../../../models/order.model';
import { OrderItem } from '../../../models/order-item.model';
import { Address } from '../../../models/address.model';
import { ProductService } from '../../../services/product.service';
import { Budget } from '../../../models/budget.model';
import { OrderItemDelivery } from '../../../models/order-item-delivery.model';
import { AuthService } from '../../../services/auth.service';
import { SKU } from '../../../models/sku.model';
import { OrderService } from '../../../services/order.service';
import { BudgetService } from '../../../services/budget.service';
import { ConfigService } from '../../../services/config.service';
import { AddressService } from '../../../services/address.service';
import { TranslateService } from '../../../services/translate.service';
import {ModalService} from '../../../services/modal.service';
import {OrderItemEudrState, OrderItemService} from '../../../services/order-item.service';
import {
  computeAvailableAddresses,
  computeMaxQuantity, orderAddressKey, setupDistributionListDeliveries, validateForSelectedAllocationGroup,
  validateOrderItem,
  validateWbsForUnique,
} from '../../../shared/helpers';
import {isEmptyArray} from '../../../shared/utils';
import {PaymentService} from '../../../services/payments.service';
import {MobileCheckServiceService} from '../../../services/mobile-check-service.service';
import {OrderItemRemove} from '../../../interfaces/order-item-remove';
import { Currency } from '../../../interfaces/currency';
import {DiscountCodeService} from '../../../services/discount-code.service';
import { ORDER_ITEM_VOID_STATE } from '../../../constants/order-item-states';
import { ALLOCATION_GROUP_MISSING } from '../../../constants/texts';
import { PnpBudget } from '../../../models/pnp-budget.model';
import { PnpBudgetService } from '../../../services/pnp-budget.service';
import { EUDRService } from '../../../services/eudr.service';
import {InventoryAllocationDelivery} from '../../../interfaces/allocation-group';
import {IDecisionPointData} from '../../../interfaces/decision-point.data';


const DETAILED_ITEM = 'detailed';
const LIST_ITEM = 'list-item';


@Component({
    selector: 'app-product-order-item',
    templateUrl: './product-order-item.component.html',
    styleUrls: ['./product-order-item.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ProductOrderItemComponent implements OnInit, OnDestroy {

    purchaseResult: {added: boolean, updated: boolean, deleted: boolean, hasError: boolean}
        = {added: false, updated: false, deleted: false, hasError: false}
    private alive = true;
    _order: Order;
    _orderItem: OrderItem;
    _originalOrderItem: OrderItem;
    productPrice: number;
    aggregatedQuantity: number;
    // groupAggregatedQuantity: number;
    customVariationTotalQuantity: number;

    totalCost = 0;

    dpQuantity = 0;
    budgets: Budget[] = [];
    selectedBudget: Budget =  null;
    selectedBudgetId: number = null;

    pnpBudgets: PnpBudget[] = null;

    dpSummaryData: IDecisionPointData;
    loadingSummaryData = false;
    decisionPointId: string;

    updatingFavorites = false;

    removingDeliveries = false;
    refreshingTrackingStatus = false;

    initialized = false;

    private initializing = false;

    checkingLease = false;
    leaseResult: string;

    private noAvailableShippingMethods = false;

    eudrFooterMessageText: string | null = null;
    productHasEUDRRestriction = false;
    private checkoutEudrRestrictedDeliveryUuids: ReadonlySet<string> = new Set<string>();
    private hasCheckoutEudrState = false;

    isOutOfStock$: Observable<boolean>;
    private selectedAllocationGroupAvailableQtyBySku: {[skuId: number]: number | undefined} = {};

    @Input() product: Product;

    @Input() viewType = DETAILED_ITEM; // detailed | list-item | grid-item

    @Input() showBudget = false;

    @Input() showEditControls = false;

    @Input() showHideItemButton = false; // only for readonly mode so far

    @Input() showTrackingInfo = false;

    @Input() showReorderButton = false;

    @Input()  canShowCustomization = false;

    @Input() showTotals = true;

    @Input() showVariations = true;

    @Input() readonly = false;

    // The cart defers off-screen items and does not display delivery-level EUDR state. Keeping this
    // opt-in avoids changing product, confirmation and historical-order views that share this component.
    @Input() cartReadonlyPerformanceMode = false;

    @Input() checkoutReadonlyPerformanceMode = false;

    @Input() set checkoutEudrState(state: OrderItemEudrState | null | undefined) {
        this.hasCheckoutEudrState = !!state;
        this.checkoutEudrRestrictedDeliveryUuids = state?.restrictedDeliveryUuids ?? new Set<string>();
        this.productHasEUDRRestriction = state?.hasRestriction ?? false;
        this.eudrFooterMessageText = state?.footerMessage ?? null;
    }

    @Input() modal = false; // true if component called from modal dialog

    @Input() hideFinancials = false;

    @Input() delivery: OrderItemDelivery;

    @Input() paidQuantity: number;

    @Input() removing = false;

    @Output() onRemove = new EventEmitter<OrderItemRemove>();
    @Output() onRemoveRestrictedItems = new EventEmitter<Order>();

    @Output() onUpdate = new EventEmitter<OrderItem>(); // output event for modal dialog to close

    @Output() close = new EventEmitter<void>();

    @Output() changed = new EventEmitter<OrderItem>();

    @Output() changeOrderItemVisibility = new EventEmitter<boolean>();


    @Output() orderItemSubmitRequired = new EventEmitter<OrderItem>();

    @Input() set order(o: Order) {
        if (!this.initializing && o  && o.loaded) {

            if (!this._order || !this._order.equal(o)) {
                this.clearSaveResult();
                this._order = o;
                if (this.product) {
                    this._orderItem = null;
                    this.setupOrderItem();
                }
            } else if (this._order.id === o.id) {
                // The rows of the item are the locations of the order, so a change among them is a
                // change to the item: re-pointing a warehouse row at another allocation group takes
                // the products ordered under the old pair with it, and the item the page holds is
                // then a row the server no longer has. Keeping its id aimed the next save at a
                // deleted row - PUT .../item/<id> answered 404 while the button still read "Update" -
                // so the item is taken from the order again, the way the list view does it.
                const addressRowsChanged = !this.sameAddressRows(o);
                this._order = o;
                if (addressRowsChanged && this.product) {
                    this.clearSaveResult();
                    this._orderItem = null;
                    this.setupOrderItem();
                    return;
                }
                // Re-apply distribution list deliveries when preset/additional addresses
                // changed but order is otherwise "equal" (same id/type/DL) (AB#83595).
                this.applyDistributionListDeliveries();
            }
        }
    };

    @Input() set orderItem( i: OrderItem) {
        if (!this._order || !this._order.loaded) {
            return;
        }

        if (!i) {
            return;
        }

        if (!this._orderItem || this._orderItem.id !== i.id) {
            this._orderItem = i;
            this._originalOrderItem = new OrderItem(i);
            this.product = this._orderItem.product;

            if (!this.initialized) {
                this.setupOrderItem();
            }
        } else {
            this._orderItem = i;
            this._originalOrderItem = new OrderItem(i);
            this.product = this._orderItem.product;
            // Re-apply distribution list deliveries: server only returns qty>0 deliveries,
            // so zero-qty preset/DL addresses must be restored after any order refresh.
            this.applyDistributionListDeliveries();
        }
    }




    constructor(private authService: AuthService,
                private addressService: AddressService,
                private productService: ProductService,
                private orderService: OrderService,
                private budgetService: BudgetService,
                private configService: ConfigService,
                private toastr: ToastrService,
                private translateService: TranslateService,
                private modalService: ModalService,
                private orderItemService: OrderItemService,
                private router: Router,
                private paymentService: PaymentService,
                private mobileCheck: MobileCheckServiceService,
                private pnpBudgetService: PnpBudgetService,
                private eudrService: EUDRService,
                private discountCodeService: DiscountCodeService
    ) {
    }

    ngOnInit(): void {
        // this.setupOrderItem();

        // Skip heavy subscriptions for readonly list-item view (cart page list).
        // These are only used in the modal/edit template and cause O(n) change
        // detection overhead when 100 items each subscribe to shared observables.
        if (!this.readonly || this.modal) {
            this.initProductUpdate();

            // Subscribe to EUDR footer message
            this.eudrService.getFooterMessage().pipe(takeWhile(() => this.alive)).subscribe(message => {
                setTimeout(() => {
                    this.eudrFooterMessageText = message;
                }, 0);
            });

            // Subscribe to restriction status
            this.eudrService.getRestrictionStatus()
                .pipe(takeWhile(() => this.alive))
                .subscribe(hasRestriction => {
                    this.productHasEUDRRestriction = hasRestriction;
                });


            this.isOutOfStock$ = this.isInventoryAllocationOutOfStock$();
        }

    }

    buttonIsDisabled(outOfStock: boolean): boolean {
      if (this.orderItemUpdating) {
          return true;
      }

      if (this.isItemInCart) {
        // product already in the cart, user can change some inputs
        return false
      }

      return !this.canPurchase
        || this.isItemCutOW
        || this.isNotSameCurrency
        || this.productHasEUDRRestriction
        || outOfStock;
    }

    ngOnDestroy() {
        this.alive = false;
    }

    // defined size of product image depends on viewType
    get imageSize(): string {
        switch (this.viewType) {
            case DETAILED_ITEM:
                return 'none';
            case LIST_ITEM:
                return 'small';
        }
    }

    get canAddAddress(): boolean {
        if (!this.product.hasVariations && !this.isDecisionPointOrder && this.canPurchase) {

            if (this.hasAvailableAddressesLeft) {
                return true;
            }

            return this.authService.canAddAddress;
        }

        return false;
    }

    get hasAvailableAddressesLeft(): boolean {
        return !isEmptyArray( computeAvailableAddresses(this.addressService.addresses, this._order));
    }


    get addressLabel(): string {
        if (this.hasAvailableAddressesLeft)  {
            return 'Add another address';
        }
        return 'Add new address';
    }

    get productCodeLabel(): string {
        return this.configService.fedInfoFeature.productCodeLabel || 'Product Code';
    }

    /**
     * Returns the variation's product code/client sku info for display.
     * - If supplier_id exists (Supplier foreign key) → returns Product Code label
     * - If supplier_id is null but client_sku_number exists → returns Client SKU label
     */
    getVariationCodeInfo(sku: SKU): { label: string, value: string } | null {
        if (sku.supplier_id) {
            return { label: this.productCodeLabel, value: sku.supplier_id };
        }

        if (sku.client_sku_number) {
            return { label: 'Client SKU', value: sku.client_sku_number };
        }

        return null;
    }

    get isBudgetExhausted (): boolean {
        if (this.budgets?.length !== 1) {
            return false;
        }
        return  this.budgets[0].isExhausted && this._orderItem.quantity >= this._orderItem.init_quantity;
    }

    get isBudgetNumber(): boolean {
        return this._orderItem?.budget != null && !Number.isNaN(this._orderItem.budget);
    }

    private async setupOrderItem() {
        if (!this._order) {
            return;
        }

        // The product is attached separately from ElasticSearch and can legitimately be
        // missing (not indexed, or the for-ids call came back empty). Everything below
        // dereferences it, and this method is called unawaited, so without this guard the
        // rejection is swallowed and the row silently never renders.
        if (!this.product) {
            console.error('ProductOrderItemComponent: no product attached to order item', this._orderItem?.id,
                'product_id', this._orderItem?.product_id);
            return;
        }


        const userRole = {canOrder: this.authService.canOrder, isEmployee: this.authService.isEmployee};

        if (!this._orderItem) {
            this._orderItem = new OrderItem(this._order.getItemForProduct(this.product, {
                defaultDelivery: null, userRole
            }));
        }
        setupDistributionListDeliveries(this._order, this._orderItem);


        this.initializing = true;

        if (this._orderItem.skus.length === 0) {
            this.product.skus.forEach((sku: SKU) => {
                this._orderItem.getVariationSKU(sku.id);
            });
        }

        this.computeAggregatedQuantity(this._orderItem.product);

        this.addEmptyShippingRowIfNone(this._orderItem);


        this.computeTotalQuantity();
        this.computeProductPrice();
        this.computeTotalCost();


        // Select first address - only for an order that has no locations of its own. When the order
        // carries preset or distribution list addresses - warehouses among them - the rows come from
        // them and the first one already has its address, which must not be traded for the user's
        // default personal address behind their back.
        if (isEmptyArray(this._order.distributionOrPresetAddresses)
            && this._orderItem.deliveries.length > 0 && !this._orderItem.deliveries[0].addressDefined) {
            this.applyDefaultAddress(this._orderItem.deliveries[0]);
        }

        this.checkDiscountCodesForDeliveries(this._orderItem);

        // if (this.readonly === false) {
        //     this.budgets = this.budgetService.getBudgetsForOrderItem(this._orderItem);
        //     this.pnpBudgetService.fetchPnpBudgets()
        //       .subscribe(pnpBudgets => {
        //           this.pnpBudgets = pnpBudgets;
        //       });
        //
        //     if (this.budgets.length > 0) {
        //         if (this._orderItem.financials_total.budget_id) {
        //             // this._orderItem.financials_total.budget_id = this.budgets[0].id;
        //             if (this._orderItem.financials_total.budget_id && !this._orderItem.budgetItem) {
        //                 this._orderItem.budgetItem = this.budgetService.getBudgetByOrderItem(this._orderItem);
        //             }
        //             this.selectedBudget = this._orderItem.budgetItem;
        //             this.selectedBudgetId = this.selectedBudget?.id;
        //         } else {
        //             const hardlimitBudget = this.budgetService.getHardlimitBudgetForProduct(this.product);
        //             if (hardlimitBudget) {
        //                 this.selectedBudget = hardlimitBudget;
        //                 this.selectedBudgetId = this.selectedBudget.id;
        //                 this._orderItem.financials_total.budget_id = hardlimitBudget.id;
        //                 this._orderItem.budgetItem = hardlimitBudget;
        //             }
        //         }
        //     }
        // }

        this._originalOrderItem = new OrderItem(this._orderItem);


        if (!this.readonly &&
            this.viewType !== 'grid-item' &&
            this._order.isWSLR !== true &&
            this._order.isDecisionPoint &&
            !this.product.hasVariations) {

            if (this.decisionPointId !== this._order.attr_decision_point_id) {
                this.decisionPointId = this._order.attr_decision_point_id;
                this.loadingSummaryData = true;
                this.dpSummaryData = await firstValueFrom(
                    this.productService.getDecisionPointData(this.decisionPointId, this._orderItem));
                this.loadingSummaryData = false;
            }
        }

        this.initialized = true;
        this.initializing = false;
    }

    checkoutDeliveryHasEudrRestriction(delivery: OrderItemDelivery): boolean {
        return this.checkoutEudrRestrictedDeliveryUuids.has(delivery.uuid);
    }

    checkoutPrecomputedEudrRestriction(delivery: OrderItemDelivery): boolean | null {
        if (!this.checkoutReadonlyPerformanceMode || !this.hasCheckoutEudrState) {
            return null;
        }
        return this.checkoutDeliveryHasEudrRestriction(delivery);
    }


    private computeProductPrice() {
        // const quantity = (this._order.type === PREORDER_TYPE) ?
        //      this._orderItem.totalQuantity : this._orderItem.quantity;

        // this.productPrice = this.product.skus[0].getPrice(quantity);
        this.productPrice = this._orderItem.getProductPrice();
    }

    private computeTotalQuantity() {
        this._orderItem.quantity = this.getTotalQty();

        this.dpQuantity = this._orderItem.quantity;

        if (!this.product.hasVariations) {
            this._orderItem.skus[0].quantity = this._orderItem.quantity;
        }

    }

    private computeTotalCost() {
        this.totalCost  = this._orderItem.financials_total.subtotal;
        // calculate discount if any
        this.totalCost = ProductService.computeDiscountPrice(this.totalCost, this.product, this._orderItem.hasTrimesterBudget);

        // if (this.product.hasVariations) {
        //     this.totalCost = 0;
        //
        //     this._orderItem.skus.forEach((sku: OrderItemSku) => {
        //         const parentSKU = this.product.getSKUbyID(sku.sku_id);
        //         if (parentSKU) {
        //             const quantity = sku.quantity;
        //             // sku.price = parentSKU.getPrice(quantity) * sku.quantity;
        //             this.totalCost += parentSKU.getPrice(quantity) * sku.quantity;
        //         }
        //     });
        // } else {
        //     this.totalCost = this._orderItem.quantity * this.productPrice;
        // }
        //

    }

    private computeAggregatedQuantity(product: Product) {
        this.aggregatedQuantity = product.getAggregatedQuantity();

        // this.groupAggregatedQuantity = (this.product.skus[0].groupMinQTY > 0 && this.product.skus[0].group_aggregated_quantity > -1)
        //     ? this.product.skus[0].group_aggregated_quantity : -1;

    }

    addAnotherAddress() {
        if (this.isItemCutOW) {
            return;
        }

        if (this.orderHasDistributionList) {
            return;
        }

        if (this.hasAvailableAddressesLeft) {
            this._orderItem = this.orderItemService.addAnotherAddress(this._orderItem, this._order);

            this.changed.emit(this._orderItem);
            setTimeout(() => {
                this.orderItemService.updateEudrStateForWholeItem(this._orderItem);
            }, 0);
        } else {
            this.modalService.editAddress(null, true).then( result => {
                if (result) {
                    this._orderItem = this.orderItemService.addAnotherAddress(this._orderItem, this._order);
                    this.changed.emit(this._orderItem);
                    setTimeout(() => {
                        this.orderItemService.updateEudrStateForWholeItem(this._orderItem);
                    }, 0);
                }
            })
        }
    }


    onUpdateDeliveryQuantity() {
        this.computeTotalQuantity();
        this.computeProductPrice();
        this.computeTotalCost();
    }


    private getTotalQty() {
        return this._orderItem.deliveries.reduce((total, delivery) => total + Number(delivery.quantity), 0);
    }

    private reduceQty(availableQty: number) {
        this._orderItem.deliveries.forEach(delivery => {
            delivery.quantity = (delivery.quantity < availableQty) ? delivery.quantity : availableQty;
            availableQty -= delivery.quantity;
        });
    }

    saveWithDelay() {
        setTimeout(() => {
            this.saveChanges();
        }, 100);
    }

    saveChanges() {
        this.clearSaveResult();

        const errorMsg = validateOrderItem(this._orderItem);
        if (errorMsg) {
            this.toastr.warning(errorMsg, 'Warning');
            return;
        }

        const internalOrderValidationError = validateWbsForUnique(this._order, this._orderItem, this.configService.wbsValidationOptions);
        if (internalOrderValidationError) {
            this.toastr.warning(internalOrderValidationError, 'Warning');
            return;
        }

        // const validationError = validateWareHouseAddresses(this._order, this._orderItem, this.configService.features);
        // if (!!validationError) {
        //   this.toastr.warning(validationError, 'Warning');
        //   return;
        // }

        if (this.isNotSameCurrency) {
            this.toastr.warning('This item is not in the same currency. Please create a new cart');
            return;
        }

        // const  checkForAllocationGroupWarning  = validateForSelectedAllocationGroup(this._orderItem);
        // if (checkForAllocationGroupWarning) {
        //     this.toastr.warning(checkForAllocationGroupWarning, 'Warning');
        //     return;
        // }

        if (this._orderItem.productHasCustomization && !this._orderItem.areAllDeliveriesCustomized) {
            this.toastr.warning('Cannot add item to cart as Customization is incomplete');
            return;
        }

        // Capture which preset addresses were explicitly deleted BEFORE the save.
        // A preset address is "explicitly deleted" when the delete button was clicked,
        // which removes its delivery from _orderItem.deliveries entirely.
        // A preset address with qty=0 still has a delivery entry and must NOT be pruned.
        const explicitlyDeletedPresetIds = new Set(
            (this._order.presetAddresses || [])
                .filter(a => !this._orderItem.deliveries.some(d => d.uniqueAddressId === a.uniqueAddressId))
                .map(a => a.uniqueAddressId)
        );

        this.validateAllocationGroups().pipe(
          switchMap(result => {
              if (!result) {
                  return of(false);
              }
              return this.orderItemService.saveOrderItem(this._order, this._orderItem, this._originalOrderItem);
          })
        ).subscribe( (result) => {
            if (typeof result  === 'boolean' && result === false) {
                return;
            } else  {
                if (typeof  result !== 'boolean' && !!result) {
                    this.purchaseResult = result.purchaseResult;

                    if (this.purchaseResult.hasError) {
                        return;
                    }

                if (result.orderItem) {
                    this._orderItem = result.orderItem;
                    this._orderItem.changed = false;
                    this._originalOrderItem = new OrderItem(this._orderItem);

                    // Only remove preset addresses that were explicitly deleted (delete button clicked).
                    if (explicitlyDeletedPresetIds.size > 0 && this._order?.presetAddresses?.length) {
                        this._order.presetAddresses = this._order.presetAddresses.filter(
                            a => !explicitlyDeletedPresetIds.has(a.uniqueAddressId)
                        );
                    }

                    this.changed.emit(null);
                } else {
                    this._orderItem = new OrderItem(this._originalOrderItem);
                }


                    if (result.order) {
                        // Propagate the same pruning to the refreshed order.
                        if (explicitlyDeletedPresetIds.size > 0 && result.order.presetAddresses?.length) {
                            result.order.presetAddresses = result.order.presetAddresses.filter(
                                a => !explicitlyDeletedPresetIds.has(a.uniqueAddressId)
                            );
                        }
                        this._order = result.order;
                    }
                    this.product = this._orderItem.product;
                    this.computeProductPrice();
                    this.computeTotalCost();


                    if (this.modal
                        && (this.purchaseResult.added || this.purchaseResult.updated || this.purchaseResult.deleted)) {
                        this.changed.emit(this._orderItem);
                    }
                } else {
                    this._orderItem = new OrderItem(this._originalOrderItem);
                }
            }
        });


    }



    private  validateAllocationGroups(): Observable<boolean> {

        const  checkForAllocationGroupWarning  = validateForSelectedAllocationGroup(this._orderItem);
        if (!checkForAllocationGroupWarning) {

            // check if allocation  groups  are the same
            if (!this.allAllocationGroupsShouldBeTheSame(this._orderItem)) {
                this.toastr.warning('You cannot have different allocation groups.');
                return of(false);
            }

            // no warnings, proceed with saving
            return of(true);
        }

        // show warning  or error
        return this.checkAllAllocationGroupsDefined$(this._orderItem?.product.isBuyingWindow).pipe(
          take(1),
          map( result => {
            if (result ) {
              this.toastr.warning(checkForAllocationGroupWarning, 'Warning');

            } else {
              this.toastr.error(ALLOCATION_GROUP_MISSING);
            }
            return false;
          })
        );

    }


    private checkAllAllocationGroupsDefined$(isBuyingWindow: boolean): Observable<boolean> {
      if (isBuyingWindow) {
        return this.productService.fetchAllocationGroups().pipe(
          take(1),
          map( group => {
            return !isEmptyArray(group);
          }));
      }
      // for on-demand products: fetch fresh so we don't piggyback on the shared inventoryAllocations$
      // BehaviorSubject (which never completes and causes leaked subscriptions to re-fire the toast).
      return this.productService.fetchInventoryAllocationForProduct$(this._orderItem?.product).pipe(
        take(1),
        map( allocations => {
          if (isEmptyArray(allocations)) {
            return false;
          }
          const onlyInStock = allocations.filter( a => a.in_stock === true);
          return onlyInStock.every( a => !isEmptyArray(a.allocation_qty));
        })
      );
    }

    // temporary solution until  it  will be  fixed  on server side
    // for  on deman products   only
    private allAllocationGroupsShouldBeTheSame(orderItem: OrderItem): boolean {
        if (!orderItem) {
            return true;
        }

        // on-demand only
        if (orderItem.product.isBuyingWindow) {
            return true;
        }

        //  collect all allocations  groups from deliveries
        let  allocationGroupId: number; // should be the same for all deliveries
        for (const delivery of orderItem.deliveries) {
            for (const ag of delivery.allocation_groups) {

                if (ag?.allocation_group_id) {

                    if (!allocationGroupId) {
                      // set allocation group id for the first time
                      allocationGroupId = ag.allocation_group_id;
                    } else {
                        if (allocationGroupId !== ag.allocation_group_id) {
                          // if allocation group id already exists in set - it means that not all groups are the same, return false
                          return false;
                        }
                    }

                }
            }
        }

        return true;
    }


    /**
     * checks whether item can be purchased
     * in case of BUYING_WINDOW project  ow_purchasable == false - purchase is not available
     */
    get canPurchase(): boolean {

        // if (this._orderItem.validateAddressesAndShipmentMethodsError()) {
        //     return  false; // cannot save order item when shipping method is not defined
        // }

        return !this.paymentService.hasFailedTransactions
            && !this.isBudgetExhausted
            && this.orderService.canPurchase(this._order, this._orderItem);
    }

    get canEditOrderItems(): boolean {
      return this._order ? this.orderService.canEditOrderItems(this._order) : false;
    }

    canAddQuantity(outOfStock: boolean): boolean {
      if (!this.canEditOrderItems) {
        return false;
      }

      if (this.isItemInCart) {
        return true;
      }
      return !outOfStock;
    }


    private isWSLROrder(): boolean {
        return this._order.isWSLR
    }

    /**
     * get actual deliveries that not removed
     * @param item order item
     */
    getActualDeliveries() {
        return this._orderItem.getActualDeliveries();
    }

    trackByDeliveryUuid(index: number, delivery: OrderItemDelivery) {
        return delivery.uuid;
    }

    updateOrderItemVariation(sku) {
        this.clearSaveResult();

        this._orderItem.validateVariationQuantity(
          sku,
          this.selectedAllocationGroupAvailableQtyBySku[sku.id]
        );

        const delivery = this.availableDelivery;
        if (delivery) {
          const qty = this._orderItem.getVariationSKU(sku.id).quantity
          delivery.saveAllocationGroupQuantity(sku.id, qty);
        }


        this.computeTotalQuantity();
        this.computeTotalCost();
        this._orderItem.changed = true;
        this.changed.emit(this._orderItem);
    }


    showDpModal() {
        this.clearSaveResult();
        this.modalService.showDecisionPoint(this._order.decisionPointData, this.dpSummaryData).then( quantity => {
            if (quantity > 0) {
                this.calculateDP(quantity);
            }
        });
    }

    calculateDP(dpQuantity) {

        this.orderItemService.calculateDecisionPoints(this._order, this._orderItem, dpQuantity).subscribe( orderItem => {
            this._orderItem = orderItem;
        });
    }



    get addresses(): Address[] {
        return this.addressService.addresses;
    }

    get canSeeBudgets(): boolean {
        return this.authService.canSeeBudgets
    }

    get isBudgetsVisible(): boolean {
        return this.canSeeBudgets && this.showBudget && !isEmptyArray(this.budgets);
    }

    get canOrder(): boolean {
        return this.authService.canOrder;
    }

    get isDecisionPointOrder(): boolean {
        return this._order?.isDecisionPoint || false;
    }

    get canRemoveAddress(): boolean {
        // not for DP orders  or  orders with distribution list
        return !this.isDecisionPointOrder && !this._order?.hasDistributionList;
    }

    get cancelled(): boolean {
        return this._orderItem ? this._orderItem.state === ORDER_ITEM_VOID_STATE : false;
    }

    get itemCancelled(): boolean {
        return !(this._orderItem?.product?.active ?? true);
    }

    onHandleRemove(hideConfirmation = false) {
        this.onRemove.emit({orderItem: this._orderItem, hideConfirmation});
    }

    onHandleRemoveRestricted() {
        this.onRemoveRestrictedItems.emit(this._order);
    }

    handleEdit() {
        if (this.mobile) {
            const product = this._orderItem.product;
            this.router.navigate([product.productURL]);
        } else {
            this.orderItemService.editOrderItemModal(this._order, this._orderItem).then( result => {
                    if (result) {
                        if (result.id) {
                            this._orderItem = result
                        }
                        this.onUpdate.emit(this._orderItem);
                    }
                }
            );
        }
    }


    // shows total cost including delivery cost, computed on backend
    get financialTotalCost(): number {
        return this.cancelled !== true ? this._orderItem.totalCost : 0;
    }

    get taxes(): number {
        return (this.cancelled !== true) ? this._orderItem.taxes : 0;
    }

    get canDisplayEditControls(): boolean {
        return (this.showEditControls === true
            // && this.cancelled === false
            && this._order.type !== 'SUMMARY_TYPE'
            && this.authService.canEditOrder(this._order)
        );
    }


    get canEditOrder(): boolean {
        return this.canDisplayEditControls &&  this.cancelled === false;
    }


    getVariationLabel(sku: SKU): string[] {
        return  sku.sortedAttributes.map( attribute => {
            const label = this.translateService.translate(attribute.label);

            return `${label}: ${attribute.options[0].label}`;
        });
    }

    onCustomizationChanged(delivery: OrderItemDelivery) {
        if (!delivery || isEmptyArray(this._orderItem?.deliveries)) {
          return;
        }

        this._orderItem.updateDelivery(delivery);
        // this.saveChanges();
    }


    get hasCustomization(): boolean {
        return this._orderItem.product.isCustomizable;
    }

    get canCustomize(): boolean {
        return this.hasCustomization &&
            (this._order && this._order.supportsCustomization && !this.paymentService.hasFailedTransactions) ;
    }


    viewPriceDetails() {
        this.modalService.showPriceDetails(null, this._orderItem);
    }

    // updatedOrderItem is the item as the server saved it; adopt it before notifying the page,
    // otherwise the stale local copy is what gets written back into the order (AB#83949 removed
    // the full items refetch that used to hide this).
    onDeliveryChanged(updatedOrderItem?: OrderItem) {
        if (updatedOrderItem) {
            this.preserveDeliveryIdentities(updatedOrderItem);
            this.orderItem = updatedOrderItem;
        }
        this.onUpdate.emit(this._orderItem);
    }

    // Deliveries are rendered with trackBy: trackByDeliveryUuid, but uuid is client-side only -
    // OrderItemDelivery mints a fresh one whenever the payload has none, and server payloads
    // never carry it. Adopting a saved item as-is would therefore change every delivery's
    // identity, so Angular would destroy and rebuild each delivery child - re-running an ngOnInit
    // that re-derives, and can clear, the shipping method on rows the user never touched. Carry
    // the currently rendered uuids across by delivery id so only genuinely new rows churn.
    private preserveDeliveryIdentities(updatedOrderItem: OrderItem) {
        if (!this._orderItem || isEmptyArray(this._orderItem.deliveries) || isEmptyArray(updatedOrderItem.deliveries)) {
            return;
        }

        const uuidByDeliveryId = new Map<number, string>(
            this._orderItem.deliveries.filter(d => !!d.id).map(d => [d.id, d.uuid] as [number, string])
        );

        updatedOrderItem.deliveries.forEach(delivery => {
            const renderedUuid = uuidByDeliveryId.get(delivery.id);
            if (renderedUuid) {
                delivery.uuid = renderedUuid;
            }
        });
    }

    onToggleFavorites(product: Product) {
        this.updatingFavorites = true;
        this.productService.toggleFavorites(product).subscribe( () => {
            this.updatingFavorites = false;
        });
    }

    onClose() {
        if (this.modal) {
            this.close.emit();
        }
    }

    private initProductUpdate() {
        if (this.viewType === DETAILED_ITEM) {
            this.productService.populateProduct(this.product);
        }

        this.productService.updatedProduct.pipe( takeWhile(() => this.alive)).subscribe( product => {
            if (this.product && this._orderItem) {
                if (product && this._orderItem.product.id === product.id) {
                    this.product = product;
                    this._orderItem.product = product;
                }
            }
        });


        if (this.readonly !== true) {
            this.productService.getInventoryAllocationForProduct(this.product);
        }

    }

    get showMinOrderInHeader(): boolean {
        if (this.readonly) {
            return false;
        }

        if (!this.product.window_id) {
            return false;
        }

        if (!this.product.hasVariations) {
            return true;
        }

        return this.product.getGroupMinQuantity() > 0;
    }


    showMinOrderInBody(sku: SKU): boolean {
        if (this.readonly) {
            return false;
        }
        if (!this.product.isBuyingWindow) {
            return false;
        }

        if (this.product.hasVariations) {
            const showMaxQuantity =   computeMaxQuantity(this.product, sku) > 0 || this.product.hasInventory;
            return !(this.product.getGroupMinQuantity() > 0) || showMaxQuantity;
        }
        return true;
    }

    get isProductRecommended(): boolean {
        return !this.readonly && this.orderItemService.isProductRecommended(this.product, this._order);
    }


    get canShowProductPurchaseDetails(): boolean {
        if (!this.product || this.product.isBuyingWindow !== true) {
            return false;
        }
        return true;
        // return this.authService.isWholeSaler || (this.authService.isEmployee && this._order?.isWSLR);
    }

    redirectToFullProduct() {
        let url = this.product.productURL;
        if (this.product.isBuyingWindow) {
            if (this._order.window_id !== this.product.window_id) {
                url = `/buying-window/${this._order.window_id}/products/${this.product.getSlug()}`;
            }
        }
        this.router.navigate([url]).then( () => this.onClose());
    }

    redirectToShoppingCart() {
        this.router.navigate([`/cart/${this._order.id}`]).then(() => this.onClose());
    }

    get orderItemUpdating(): boolean {
        return this.orderItemService.updating;
    }

    onOrderItemChanged(orderItem: OrderItem) {
        if (orderItem) {
            this.clearSaveResult();
            this._orderItem = new OrderItem(orderItem);
            this._orderItem.changed = true;
            this._orderItem.computeAvailableAddresses();
            // this._order.updateOrderItems(orderItem);
            this.orderItemService.prefetchCapexForEntity(this._orderItem);

            // const errorMsgs  =  this._orderItem.validateAddressesAndShipmentMethodsError();
            // if (errorMsgs) {
            //     this.toastr.warning(errorMsgs);
            //     return;
            // }
            this.changed.emit(this._orderItem);
        }
    }

    onOrderItemChangeVisibility(val: boolean) {
        this._orderItem.hidden = val;
        this.changeOrderItemVisibility.emit(val);
    }

    public reorderItem() {
        if (!this._order || !this.product) {
            return;
        }
        this.orderItemService.showProductInModal(this.product, this._order, this.showReorderButton === true).subscribe();
    }

    refreshTrackingStatus() {

        if (this.refreshingTrackingStatus) {
            return; // avoid several clicks
        }

        this.refreshingTrackingStatus = true;
        this.orderService.getTrackingData(this._order).subscribe(retOrder => {
            if (retOrder) {
                this._order =  retOrder;
                this._orderItem = this._order.findOrderItem(this._orderItem.id);
            }
            this.refreshingTrackingStatus = false;
        });
    }


    showDPFaqDialog() {
        this.modalService.showDecisionPointFaq(this._order.attr_decision_point_name);
    }

    private clearSaveResult(): void {
        this.purchaseResult = {added: false, updated: false, deleted: false, hasError: false};
    }

    changeBudget(budget: Budget) {
        this.selectedBudget = budget;
        this.selectedBudgetId = this.selectedBudget.id;
        this._orderItem.financials_total.budget_id = budget.id;
    }

    checkLeaseStatus() {
        this.checkingLease = true;
        this.leaseResult = '';
        this.orderItemService.checkLeaseStatus().subscribe( data => {
            this.checkingLease = false;
            this.leaseResult = data;
        });
        return false;
    }


    get canShowDPModal(): boolean {
        return !this.paymentService.hasFailedTransactions;
    }

    get mobile(): boolean {
        return this.mobileCheck.mobile;
    }

    get mobileDevice(): boolean {
        return this.mobileCheck.mobileDevice;
    }

    get tabletDevice(): boolean {
        return this.mobileCheck.tabletDevice;
    }

    get showLeaseWarning(): boolean {
        return this.orderItemService.shouldShowLeaseWarning(this.product);
    }

    get leaseAgreementUrl(): string {
        return '';
    }

    get isTaxExemptAllowed(): boolean {
        return this.authService.user.isTaxExemptAllowed || this._order.isWSLR;
    }

    get taxExemptOrderHasTaxes(): boolean {
        if (!this.isTaxExemptAllowed) {
        return false;
        }

        if (!this._order.taxExempt ) {
            return false;
        }

        if (this._orderItem.taxes === 0) {
            return false;
        }

        return !this._orderItem.taxExemptIncluded;

    }


    get taxExemptReason(): string {
        if (!this.taxExemptOrderHasTaxes) {
            return  '';
        }

        return this._orderItem.taxExemptReason;
    }

    get restricted(): boolean {
        return this.product.restricted;
    }

    get containerCSSClass(): string {
        return `${this.viewType} ${this.restricted ? 'container-disabled' : ''}`;
    }

    get orderCurrency(): Currency {
        return this._orderItem?.product?.currency ?? this._order?.currency;
    }

    get isItemCutOW(): boolean {
        return !!(this._orderItem?.item_cut || this.product?.item_cut);
    }

    // product has been already added to the cart
    get isItemInCart(): boolean {
        return !!this._orderItem?.id;
    }
    get purchaseButtonTitle(): string {
        if (this.isNotSameCurrency) {
            return 'Currency differs from products in cart';
        }

        if (this.canPurchase) {
            return this._orderItem.id ? 'Update' : 'Add to cart';
        }



        return '';
    }

    get isNotSameCurrency(): boolean {
        return this._order.items.some(orderItem => {
            const result =  !isEqual(orderItem.product.currency, this._orderItem.product.currency);
            return result;
        });
    }

    get orderHasDistributionList(): boolean {
        return this._order.hasDistributionList;
    }

    get dlDeliveries(): OrderItemDelivery[] {
        if (!this.orderHasDistributionList || !this._order.distributionList?.addresses) {
            return [];
        }
        return this.getActualDeliveries().slice(0, this._order.distributionList.addresses.length);
    }

    get additionalDeliveries(): OrderItemDelivery[] {
        if (!this.orderHasDistributionList || !this._order.distributionList?.addresses) {
            return [];
        }
        const deliveries = this.getActualDeliveries().slice(this._order.distributionList.addresses.length);
        deliveries.forEach(d => d.isAdditional = true);
        return deliveries;
    }

    addAdditionalAddress() {
        if (this.isItemCutOW) {
            return;
        }
        if (this.hasAvailableAddressesLeft) {
            this._orderItem = this.orderItemService.addAnotherAddress(this._orderItem, this._order);
            this.changed.emit(this._orderItem);
            setTimeout(() => {
                this.orderItemService.updateEudrStateForWholeItem(this._orderItem);
            }, 0);
        } else {
            this.modalService.editAddress(null, true).then(result => {
                if (result) {
                    this._orderItem = this.orderItemService.addAnotherAddress(this._orderItem, this._order);
                    this.changed.emit(this._orderItem);
                    setTimeout(() => {
                        this.orderItemService.updateEudrStateForWholeItem(this._orderItem);
                    }, 0);
                }
            });
        }
    }


    handleOrderItemSubmitRequired(event: boolean) {
        if (event) {
            this.orderItemSubmitRequired.emit(this._orderItem);
        }
    }

    checkDiscountCodesForDeliveries(orderItem: OrderItem) {
        if (!this._order.isEditable) {
            return;
        }

        if (isEmptyArray(orderItem.deliveries)) {
            return;
        }

        // AB#84030: drop coupons that are no longer active discount codes (the discount was
        // deactivated/expired after being assigned to the address). setAddress() copies the
        // address' app_coupon_id onto the delivery, and sending a stale coupon makes the API
        // reject the order update and zero out the cart totals.
        for (const delivery of orderItem.deliveries) {
            const couponId = delivery.discount_code?.id || delivery.app_coupon_id;
            if (couponId && !this.discountCodeService.getDiscountCodeById(couponId)) {
                delivery.app_coupon_id = null;
                delivery.discount_code = null;
            }
        }

        // for (const delivery of orderItem.deliveries) {
        //     if (!delivery.app_coupon_id) {
        //         const foundDiscounts  = this.discountCodeService.getDiscountCodesForAddress(delivery.getAddress());
        //         // in case only one discount code is found  apply this discount code
        //         if (foundDiscounts?.length === 1) {
        //             delivery.app_coupon_id = foundDiscounts[0].id;
        //         }
        //     }
        // }

    }

    get hasDiscountErrors(): boolean {
        return this._orderItem.hasDiscountErrors;
    }

    get hasValidDiscount(): boolean {
        return this._orderItem.totalDiscount > 0 && !this.hasDiscountErrors;
    }

    get showCustomizationButton(): boolean {
      return this.canShowCustomization && this.product.isCustomizable;
    }

    onEdited(orderItem: OrderItem) {
      if (orderItem) {
        this._orderItem = new OrderItem(orderItem);
        this.onUpdate.emit(this._orderItem);
      }
    }

    onRestrictionChange(value: boolean): void {
        this.productHasEUDRRestriction = value;
    }

    isInventoryAllocationOutOfStock$(skuId?: number): Observable<boolean> {
        if (!this.product
          || this.product.isBuyingWindow
          || !this.product.isEnabledAllocatedInventory) {
            return of(false);
        }

        if (this.product.hasVariations) {
            return this.productService.isProductInventoryAllocationOutOfStock(this.product.id);
        }

        return this.productService.isInventoryAllocationOutOfStock(this.product?.id, skuId);
    }

    isProductInventoryAllocationOutOfStock(): Observable<boolean> {
        if (!this.product
          || this.product.isBuyingWindow
          || !this.product.isEnabledAllocatedInventory) {
            return of(false);
        }

        return this.productService.isProductInventoryAllocationOutOfStock(this.product?.id);

    }

    get hasBudget(): boolean {
        return this._orderItem?.hasBudget;
    }

    /** Whether an order carries the same locations, group and all, as the one the item was built from. */
    private sameAddressRows(order: Order): boolean {
        const keys = (o: Order): string => (o?.distributionOrPresetAddresses || [])
            .filter(address => address != null)
            .map(address => orderAddressKey(address))
            .sort()
            .join(',');

        return keys(this._order) === keys(order);
    }

    private applyDistributionListDeliveries(): void {
        if (!this._order) {
            return;
        }
        if (this._orderItem) {
            setupDistributionListDeliveries(this._order, this._orderItem);
            this.addEmptyShippingRowIfNone(this._orderItem);
        }
        if (this._originalOrderItem) {
            setupDistributionListDeliveries(this._order, this._originalOrderItem);
            this.addEmptyShippingRowIfNone(this._originalOrderItem);
        }
    }

    // An item whose every preset is a warehouse it ignores (no allocated inventory) is left without
    // rows; it still needs one to pick an address in, or the delivery block renders empty. The row
    // starts on the user's default address, as it does for an order without locations of its own.
    private addEmptyShippingRowIfNone(orderItem: OrderItem): void {
        if (this.isDecisionPointOrder || orderItem.deliveries.length > 0) {
            return;
        }
        const delivery = new OrderItemDelivery({
            type: 'SHIPPING',
            method: '',
            quantity: 0,
            data: {}
        });
        this.applyDefaultAddress(delivery);
        orderItem.deliveries.push(delivery);
    }

    private applyDefaultAddress(delivery: OrderItemDelivery): void {
        if (!this.product) {
            return;
        }
        const userRoles = {canOrder: this.authService.canOrder, isEmployee: this.authService.isEmployee};
        if (this._order.isWSLR) {
            // set default WLS address
            const entityAddress = Address.fromEntityLocation(this._order.entity.getDefaultShippingLocation());
            if (entityAddress) {
                delivery.setAddress(entityAddress, this.authService.user, userRoles, this.product);
            }
        } else  {
            // set user's default address
            const availableAddresses = computeAvailableAddresses(this.addressService.allShipmentAddresses, this._order);

            const addresses = this.product.isInternationalShipmentAllowed ?
                availableAddresses : availableAddresses.filter( a => a.isInternational !== true);
            if (!isEmptyArray(addresses)) {
                // find first default
                const address = addresses.find( a => a.isDefault);
                if (address) {
                    delivery.setAddress(address, this.authService.user, userRoles, this.product);
                }
            }
        }
    }

    onAllocationGroupChanged(allocation: InventoryAllocationDelivery): void {
        if (!this.canEditOrderItems) {
            return;
        }

        if (!allocation) {
            return;
        }

        const delivery = this.availableDelivery;
        if (!delivery) {
          return;
        }

        delivery.saveAllocationGroup(allocation.sku_id, {allocation_group_id: allocation.allocation_group_id,
            allocation_group_label: allocation.allocation_group_label});

        this.productService.getInventoryAllocationForSku(this.product?.id, allocation.sku_id)
            .pipe(take(1))
            .subscribe(inventoryAllocation => {
                const selectedAllocation = inventoryAllocation.allocation_qty
                    .find(ag => ag.allocation_group_id === allocation.allocation_group_id);
                this.selectedAllocationGroupAvailableQtyBySku[allocation.sku_id] = selectedAllocation?.available_qty;

                const sku = this.product?.getSKUbyID(allocation.sku_id);
                if (sku) {
                    this._orderItem.validateVariationQuantity(sku, selectedAllocation?.available_qty);
                    this.computeTotalQuantity();
                    this.computeTotalCost();
                }

                this.clearSaveResult();
                this._orderItem.changed = true;
                this.changed.emit(this._orderItem);
            });
    }

    get availableDelivery(): OrderItemDelivery | null {
      if (this.delivery) {
        return this.delivery;
      }
      if (isEmptyArray(this.getActualDeliveries())) {
        return null;
      }

      return this.getActualDeliveries()[0];
    }

    get isBuyingWindow(): boolean {
        return this._orderItem?.product.isBuyingWindow;
    }

    get hasVariations(): boolean {
        return this._orderItem?.product.hasVariations;
    }

    get isEnabledAllocatedInventory(): boolean {
        return this._orderItem?.product.isEnabledAllocatedInventory;
    }

    get hideOnDemandSkuAllocationControl(): boolean {
        return this.isBuyingWindow || !this.hasVariations || !this.isEnabledAllocatedInventory;
    }

}
